"use client";

import { Label } from "@wtc/ui";
import { ClientSelectWithModal } from "@/components/ui/selects/components/ClientSelectWithModal";
import { CompanySelectWithModal } from "@/components/ui/selects/components/CompanySelectWithModal";
import { SmartCompanyDisplay } from "@/components/shared/smart-display/SmartCompanyDisplay";
import { SmartContactDisplay } from "@/components/shared/smart-display/SmartContactDisplay";
import type { UseFormSetValue } from "react-hook-form";
import type { DocumentFormData } from "../msp-document-schema";

interface CustomerContextFieldsProps {
  category: DocumentFormData["category"];
  companyId?: string;
  clientId?: string;
  setValue: UseFormSetValue<DocumentFormData>;
}

/**
 * Company / contact pickers with the "one or the other" behavior. Required
 * (visually flagged) only for the customer-specific category. Extracted so the
 * wizard's Organize step and any legacy form can share one implementation.
 */
export function CustomerContextFields({
  category,
  companyId,
  clientId,
  setValue,
}: CustomerContextFieldsProps) {
  const isCustomerSpecific = category === "client-specific";

  return (
    <div className="space-y-4 rounded-lg border border-border bg-muted/30 p-3">
      <div>
        <p className="text-sm font-medium text-foreground">Customer context</p>
        <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
          <strong>Company</strong> is the customer organization; <strong>Contact</strong> is a
          person in your CRM. Set one or the other—choosing a company clears the contact, and vice
          versa. For the <strong>Customer-specific</strong> category, at least one is required.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="company_id">
          Company
          {isCustomerSpecific && !clientId && (
            <span className="ml-1 text-destructive" aria-hidden>
              *
            </span>
          )}
        </Label>
        <CompanySelectWithModal
          value={companyId || ""}
          onValueChange={(value) => {
            setValue("company_id", value);
            if (value) setValue("client_id", "");
          }}
          placeholder={
            isCustomerSpecific && !clientId
              ? "Select company (required if no contact)..."
              : "Search or select a company (optional)..."
          }
          fullWidth
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="client_id">
          Contact
          {isCustomerSpecific && !companyId && (
            <span className="ml-1 text-destructive" aria-hidden>
              *
            </span>
          )}
        </Label>
        <ClientSelectWithModal
          value={clientId || ""}
          onValueChange={(value) => {
            setValue("client_id", value);
            if (value) setValue("company_id", "");
          }}
          placeholder={
            isCustomerSpecific && !companyId
              ? "Select contact (required if no company)..."
              : "Search or select a contact (optional)..."
          }
          fullWidth
        />
        {isCustomerSpecific && !clientId && !companyId && (
          <p className="text-sm text-destructive">
            Set a company or a contact for customer-specific documents
          </p>
        )}
      </div>

      {(companyId || clientId) && (
        <div className="space-y-1 rounded-md border bg-background/80 p-2 text-sm">
          {companyId && (
            <div className="flex items-center justify-between gap-2">
              <span className="shrink-0 text-xs text-muted-foreground">Company</span>
              <SmartCompanyDisplay companyId={companyId} showIcon={false} fallbackText="Unknown company" />
            </div>
          )}
          {clientId && (
            <div className="flex items-center justify-between gap-2">
              <span className="shrink-0 text-xs text-muted-foreground">Contact</span>
              <SmartContactDisplay contactId={clientId} showIcon={false} fallbackText="Unknown contact" />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
